import { Grid, Skeleton } from "@mui/material";
import classNames from "classnames";
import { useState } from "react";
import { useSubscription } from "../../hooks/useSubscription";
import useTranslation from "../../hooks/useTranslation";
import SurveyDispatch from "../../models/surveyDispatch";
import { InvalidRespondentsResponse } from "../../types/survey";
import { SubscriptionActions } from "../../types/user";
import { getFormattedDate } from "../../utils/formatters";
import { ProtectedRoutes, SurveyRoutes } from "../../utils/routes.utils";
import { SurveyDispatchEngagement } from "../SurveyDispatchEngagement/SurveyDispatchEngagement";
import { SurveyLaunchButton } from "../SurveyLaunchButton/SurveyLaunchButton";
import { SurveyPublicLink } from "../SurveyPublicLink/SurveyPublicLink";
import { SurveyReadyStatus } from "../SurveyReadyStatus/SurveyReadyStatus";
import { SurveyRecipients } from "../SurveyRecipients/SurveyRecipients";
import { SurveySettings } from "../SurveySettings/SurveySettings";
import { Button } from "../ui/Button/Button";
import { CircularProgressBar } from "../ui/CircularProgressBar/CircularProgressBar";
import { Modal } from "../ui/Modal/Modal";
import { Paper } from "../ui/Paper/Paper";
import { Tooltip } from "../ui/Tooltip/Tooltip";
import { Typography, TypographyVariant } from "../ui/Typography/Typography";
import { IconTypes } from "../ui/icons";
import { Icon } from "../ui/icons/Icon";
import "./SurveyDispatchStatusCard.scss";

interface SurveyDispatchStatusCardProps {
  className?: string;
  surveyDispatch?: SurveyDispatch;
  isLoading: boolean;
  onSurveyUpdate?: () => void;
  invalidRespondents?: InvalidRespondentsResponse;
  invalidRespondentsError?: boolean;
}

export const SurveyDispatchStatusCard = ({
  className,
  surveyDispatch,
  isLoading,
  onSurveyUpdate,
  invalidRespondents,
  invalidRespondentsError,
}: SurveyDispatchStatusCardProps) => {
  const { t, i18n } = useTranslation("dashboardPage");
  const { hasAccess } = useSubscription();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSurveySetting, setIsSurveySetting] = useState(false);
  const [isShareSurvey, setIsShareSurvey] = useState(false);

  const handleModalClose = () => {
    setIsModalOpen(false);
    setIsShareSurvey(false);
    setIsSurveySetting(false);
  };

  const renderHeader = (
    title: string,
    actionTitle: string,
    actionId: string,
    icon: IconTypes,
    options: {
      isMain?: boolean;
      showAction?: boolean;
      href?: string;
      action?: () => void;
    }
  ) => {
    const titleSizeDesktop = options.isMain ? "h3" : "body1";
    const titleSizeMobile = options.isMain ? "body1" : "body2";
    const classes = classNames("SurveyDispatchStatusCard__header", {
      "SurveyDispatchStatusCard__header--main": options.isMain,
    });

    return (
      <Grid container className={classes}>
        <Grid item xs={options.showAction ? 11 : 12}>
          {isLoading && <Skeleton height={20} width={50} />}
          <Typography
            desktop={titleSizeDesktop as TypographyVariant}
            mobile={titleSizeMobile as TypographyVariant}
            weight="bold"
          >
            {title}
          </Typography>
        </Grid>
        <Grid item xs={options.showAction ? 1 : 0}>
          {options.showAction && (
            <Tooltip title={actionTitle} arrow>
              <Button
                onClick={() => options.action?.()}
                data-testid={`action-${actionId}`}
                aria-label={actionTitle}
                aria-controls={`action-${actionId}`}
                aria-haspopup="true"
                variant="contained"
                color="secondary"
                xSmall
                icon
                href={options.href}
              >
                {<Icon type={icon} size={14} />}
              </Button>
            </Tooltip>
          )}
        </Grid>
      </Grid>
    );
  };

  const renderNotLaunched = () => {
    if (!surveyDispatch || !surveyDispatch.hasNotLaunched()) return null;

    const msg = surveyDispatch.isInProgress()
      ? t("workplaceCultureWidget.latestCard.inProgressMsg")
      : t("workplaceCultureWidget.latestCard.readyMsg", {
          launchDate: getFormattedDate(
            surveyDispatch.launchDate || "",
            t,
            i18n.language
          ),
        });

    return (
      <div>
        <div className="SurveyDispatchStatusCard__notLaunched">
          <Typography desktop="body2" mobile="caption">
            {msg}
          </Typography>
          <SurveyReadyStatus
            className="SurveyDispatchStatusCard__notLaunched__status"
            surveyDispatch={surveyDispatch}
          />
        </div>
      </div>
    );
  };

  const renderActions = () => {
    if (!surveyDispatch) return null;

    if (
      surveyDispatch.isActive() ||
      !hasAccess(SubscriptionActions.LAUNCH_SURVEY)
    ) {
      return <div></div>;
    }

    const actionDefinitions = {
      editSurvey: {
        id: "edit-survey",
        title: surveyDispatch.isReady()
          ? t("workplaceCulturePage:settings.viewSurveySetup")
          : t("workplaceCulturePage:settings.editSurvey"),
        link: `${ProtectedRoutes.LAUNCH}/${surveyDispatch.id}`,
      },
      newSurvey: {
        id: "new-survey",
        title: t("workplaceCulturePage:settings.newSurvey"),
        link: undefined,
      },
    };

    const action = surveyDispatch.hasNotLaunched()
      ? actionDefinitions.editSurvey
      : actionDefinitions.newSurvey;

    const classes = classNames("SurveyDispatchStatusCard__actions", {
      "SurveyDispatchStatusCard__actions--notLaunched":
        surveyDispatch.hasNotLaunched(),
    });

    const ButtonElement = surveyDispatch.hasNotLaunched()
      ? Button
      : SurveyLaunchButton;

    return (
      <div className={classes}>
        <ButtonElement
          key={action.id}
          data-testid={`action-${action.id}`}
          aria-label={action.title}
          aria-controls={`action-${action.id}`}
          aria-haspopup="true"
          variant="contained"
          color={surveyDispatch.hasNotLaunched() ? "info" : "secondary"}
          small={!surveyDispatch.hasNotLaunched()}
          {...(action.link && { href: action.link })}
          {...(!surveyDispatch.hasNotLaunched() && {
            hasActiveDispatches: surveyDispatch.isActive(),
          })}
        >
          {action.title}
        </ButtonElement>
      </div>
    );
  };

  const renderSurveyPeriod = () => {
    if (!surveyDispatch || surveyDispatch.hasNotLaunched()) return null;

    const formattedLaunchDate = surveyDispatch.launchDate
      ? getFormattedDate(surveyDispatch.launchDate, t, i18n.language)
      : "";
    const formattedDueDate = surveyDispatch.dueDate
      ? getFormattedDate(surveyDispatch.dueDate, t, i18n.language)
      : "";

    const dueDateText = surveyDispatch.isClosed() ? "closed" : "dueDate";

    return (
      <Paper secondary className="SurveyDispatchStatusCard__period" compact>
        {renderHeader(
          t(
            `workplaceCultureWidget.latestCard.${
              surveyDispatch.isClosed() ? "duration" : "durationAndReminder"
            }`
          ),
          t("workplaceCulturePage:settings.dueDate"),
          "due-date",

          IconTypes.Edit,
          {
            isMain: false,
            showAction: surveyDispatch.isActive(),
            action: () => {
              setIsSurveySetting(true);
              setIsModalOpen(true);
            },
          }
        )}
        <div className="SurveyDispatchStatusCard__period__progress">
          <Grid container>
            <Grid
              item
              md={3}
              xs={12}
              className="SurveyDispatchStatusCard__period__progress__bar"
            >
              <CircularProgressBar
                progress={surveyDispatch.getProgress()}
                color="success"
              />
            </Grid>
            <Grid item md={9} xs={12}>
              <Grid container>
                <Grid item xs={12}>
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <Typography desktop="caption">
                        {formattedLaunchDate}
                      </Typography>
                      <Typography
                        desktop="caption"
                        className="SurveyDispatchStatusCard__period__progress__dates"
                      >
                        {t("workplaceCultureWidget.latestCard.launchDate")}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography desktop="caption">
                        {formattedDueDate}
                      </Typography>
                      <Typography
                        desktop="caption"
                        className="SurveyDispatchStatusCard__period__progress__dates"
                      >
                        {t(`workplaceCultureWidget.latestCard.${dueDateText}`)}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid
                  item
                  md={12}
                  sx={{
                    display: {
                      xs: "none",
                      md: surveyDispatch.isClosed() ? "none" : "block",
                    },
                  }}
                >
                  <Typography
                    desktop="caption"
                    className="SurveyDispatchStatusCard__period__progress__reminder"
                  >
                    {t("workplaceCultureWidget.latestCard.reminders", {
                      count: surveyDispatch?.reminders.length,
                    })}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </div>
      </Paper>
    );
  };

  const renderEngagement = () => {
    if (!surveyDispatch || surveyDispatch.hasNotLaunched()) return null;

    const actionTitle = surveyDispatch.isPublic
      ? t("workplaceCulturePage:settings.share")
      : t("workplaceCulturePage:settings.addRecipients");

    return (
      <Paper secondary className="SurveyDispatchStatusCard__engagement" compact>
        {renderHeader(
          t("workplaceCultureWidget.latestCard.engagement"),
          actionTitle,
          "engagement",

          IconTypes.Share,
          {
            isMain: false,
            showAction: surveyDispatch.isActive(),
            action: () => {
              setIsShareSurvey(true);
              setIsModalOpen(true);
            },
          }
        )}

        <SurveyDispatchEngagement
          surveyDispatch={surveyDispatch}
          invalidRespondents={invalidRespondents}
          invalidRespondentsError={!!invalidRespondentsError}
        />
      </Paper>
    );
  };

  const renderShareSurvey = () => {
    if (surveyDispatch?.isPublic) {
      return <SurveyPublicLink surveyDispatch={surveyDispatch} />;
    }
    return (
      <SurveyRecipients
        onSurveyUpdate={onSurveyUpdate}
        surveyDispatch={surveyDispatch}
        isLoading={isLoading}
      />
    );
  };

  const classes = classNames("SurveyDispatchStatusCard", className, {
    "SurveyDispatchStatusCard--noSurvey": !surveyDispatch,
  });

  return (
    <Paper className={classes}>
      {renderHeader(
        isLoading
          ? ""
          : t(
              `workplaceCultureWidget.latestCard.title${
                surveyDispatch?.getStatusLabel(false) || ""
              }`
            ),
        t("workplaceCulturePage:settings.preview"),
        "preview",
        IconTypes.Preview,

        {
          isMain: true,
          showAction:
            !isLoading && hasAccess(SubscriptionActions.LAUNCH_SURVEY),
          href: `${SurveyRoutes.PREVIEW_SURVEY}/${surveyDispatch?.id}`,
        }
      )}

      <div className="SurveyDispatchStatusCard__content">
        {isLoading && <Skeleton height={200} />}
        {renderNotLaunched()}
        {renderActions()}
        {renderSurveyPeriod()}
        {renderEngagement()}
      </div>
      <Modal
        open={isModalOpen}
        onClose={handleModalClose}
        light
        className="SurveyDispatchCard__modal"
      >
        {isSurveySetting && surveyDispatch && (
          <SurveySettings
            dispatch={surveyDispatch}
            onSurveyUpdate={() => {
              onSurveyUpdate?.();
              handleModalClose();
            }}
          />
        )}
        {isShareSurvey && renderShareSurvey()}
      </Modal>
    </Paper>
  );
};
